import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';

const getScoreColor = (score, maxScore) => {
  const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
  if (pct >= 75) return 'success';
  if (pct >= 50) return 'warning';
  return 'error';
};

const DimensionScoreCard = ({ dimension, onEvidenceClick }) => {
  if (!dimension) return null;

  const { name, score, maxScore, weight, evidence = [], commentary } = dimension;
  // The evaluator left this dimension out: shown as not scored, not as 0 (PD-08).
  const notScored = dimension.missing || score === null || score === undefined;
  const pct = !notScored && maxScore > 0 ? (score / maxScore) * 100 : 0;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>{name}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {notScored ? (
            <Chip label="Not scored" size="small" color="info" variant="outlined" />
          ) : (
            <Typography variant="h6" fontWeight={700} color={`${getScoreColor(score, maxScore)}.main`}>
              {score}/{maxScore}
            </Typography>
          )}
          {weight > 1 && (
            <Chip label={`${weight}x weight`} size="small" variant="outlined" />
          )}
        </Box>
      </Box>

      <LinearProgress
        variant="determinate"
        value={pct}
        color={notScored ? 'inherit' : getScoreColor(score, maxScore)}
        sx={{ mb: 1, height: 6, borderRadius: 3 }}
      />

      {commentary && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {commentary}
        </Typography>
      )}

      {evidence.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" fontWeight={600}>Evidence:</Typography>
          {evidence.map((ev, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                gap: 1,
                mt: 0.5,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                p: 0.5,
                borderRadius: 1,
              }}
              onClick={() => onEvidenceClick && onEvidenceClick(ev.turnIndex)}
            >
              <Chip label={`Turn ${ev.turnIndex}`} size="small" color="primary" variant="outlined" />
              <Typography variant="caption">{ev.comment}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default DimensionScoreCard;
